/**
 * PDP wishlist heart.
 *
 * Swym renders its own icon inside #swym-atw-pdp-button-icon and REWRITES that
 * SVG afterwards — on init, on add/remove, and again when its async wishlist
 * state arrives. A one-shot swap does not hold, so this is one of the few places
 * a permanent observer is genuinely warranted; it is the same conclusion the AB
 * Tasty variation reached (ab-figma-usp.js L455-464 keeps its icon observer
 * forever and explains why a 5-minute disconnect produced a blank heart on any
 * add that happened later than that).
 *
 * The swap sets the viewBox as well as the path. Swym's default glyph is drawn on
 * a 24x24 canvas, so writing only the path leaves our heart scaled wrongly inside
 * Swym's viewBox — that is the "big partial heart" symptom the variation records
 * at ab-figma-usp.js L370-376.
 *
 * `vector-effect: non-scaling-stroke` keeps the 2px stroke at 2 screen pixels
 * rather than multiplying it by the viewBox-to-CSS-size transform.
 *
 * Idempotent and non-re-entrant: every write is guarded by a compare against the
 * value we are about to write, so the observer's own mutations settle in one pass
 * and steady state is a handful of reads per animated frame with no writes.
 */
(function () {
  'use strict';

  var ICON_ID = 'swym-atw-pdp-button-icon';
  var HEART_CLASS = 'pdp-wishlist__heart';

  /* Figma's own export box, kept verbatim: viewBox `0 0 20 18` is 10:9 while
     the design's icon slot is 20 x 17.143px (7:6), so the default
     xMidYMid/meet scales the glyph by min(20/20, 17.143/18) = 0.95238 and
     centres it. That letterboxing is what the design and the previously
     shipped markup both show, so it is deliberate rather than a bug to
     "fix" with a tightened viewBox.

     It does mean a geometric stroke would paint at 1.905px, which is why
     `vector-effect: non-scaling-stroke` below is load bearing: it pins the
     stroke at the specified 2 screen pixels regardless of that transform. */
  var VIEWBOX = '0 0 20 18';
  var PATH =
    'M11.9795 2.20605C13.5738 0.610195 16.176 0.596178 17.7871 2.17578' +
    'C19.3929 3.75029 19.4055 6.3126 17.8164 7.90332L10 15.7285L2.18359 7.90332' +
    'C0.594467 6.3126 0.607068 3.75029 2.21289 2.17578C3.82396 0.596179 6.42619 0.610196 8.02051 2.20605' +
    'L9.29297 3.47852L10 4.18652L10.707 3.47852L11.9795 2.20605Z';

  var SVG_NS = 'http://www.w3.org/2000/svg';
  var frame = null;

  /**
   * Swym can shrink or hide the icon through an inline style on an async state
   * change. Stylesheet rules cannot outrank an inline style, and the theme is not
   * allowed a blanket `!important` here, so the two properties Swym is known to
   * write are removed from the inline style instead. Only ever removes; anything
   * else on the style attribute is left alone.
   */
  function clearInlineSizing(icon) {
    if (!icon.hasAttribute('style')) return;
    var s = icon.style;
    if (s.width) s.removeProperty('width');
    if (s.height) s.removeProperty('height');
    if (s.display === 'none') s.removeProperty('display');
    if (s.visibility === 'hidden') s.removeProperty('visibility');
    /* An emptied style attribute is removed so the next pass early-returns. */
    if (!icon.getAttribute('style')) icon.removeAttribute('style');
  }

  function applyIcon() {
    frame = null;
    var icon = document.getElementById(ICON_ID);
    if (!icon) return;

    clearInlineSizing(icon);

    /* Asserted before the path guard, and only when it differs: Swym sometimes
       resets just the viewBox, which the path guard below would early-return past
       (ab-figma-usp.js L399-401 hit the same case). Writing only on a real change
       is what stops the observer re-triggering itself. */
    if (icon.getAttribute('viewBox') !== VIEWBOX) {
      icon.setAttribute('viewBox', VIEWBOX);
    }

    /* Match OUR marked path specifically. A Swym-injected default path — which
       the stylesheet already hides — must still count as "not ours" so it gets
       replaced rather than left in place. */
    var ours = icon.querySelector('path.' + HEART_CLASS);
    if (ours && ours.getAttribute('d') === PATH) return;

    if (icon.getAttribute('fill') !== 'none') icon.setAttribute('fill', 'none');

    /* Rebuilt, not reused: Swym's own path carries stroke-linecap and
       stroke-width attributes that would otherwise survive and fight the design's
       flat 2px stroke. */
    while (icon.firstChild) icon.removeChild(icon.firstChild);

    var path = document.createElementNS(SVG_NS, 'path');
    path.setAttribute('d', PATH);
    /* The marker the stylesheet keys off: it hides every other child of the icon
       so a Swym-injected 24x24 heart never paints, even for a frame. */
    path.setAttribute('class', HEART_CLASS);
    /* Presentation attributes are the no-CSS fallback only. The stylesheet owns
       the palette (see pdp/title-row.scss) because a CSS declaration outranks a
       presentation attribute — which is how the added state can repaint this same
       path without JS having to know about Swym's state classes. */
    path.setAttribute('fill', '#ffffff');
    path.setAttribute('stroke', '#333333');
    path.setAttribute('stroke-width', '2');
    path.setAttribute('vector-effect', 'non-scaling-stroke');
    icon.appendChild(path);
  }

  function schedule() {
    if (frame !== null) return;
    frame =
      typeof window.requestAnimationFrame === 'function'
        ? window.requestAnimationFrame(applyIcon)
        : setTimeout(applyIcon, 16);
  }

  /**
   * Consent-gated recovery of the heart (the EU store, and any store with
   * `theme.consentGating` on).
   *
   * Swym's PDP app block gates its own button on Shopify's Customer Privacy
   * API (addtowishlistbutton.js, checkConsent): `preferencesProcessingAllowed()`
   * true -> the button is shown; `false` -> the button node is REMOVED from
   * the DOM outright; undefined -> it keeps polling. On a consent-gated store
   * the theme (snippets/cookie-consent-shopify-sync.liquid) deliberately sets
   * every category to false — `preferences` included — until Cookiebot reports
   * the shopper has decided, and it does so at load, DOMContentLoaded and
   * `load`, i.e. also in the window before Cookiebot's own script has even
   * arrived. So for a first-time visitor, a private tab, a Safari session
   * whose cookies were cleared, or simply a slow mobile network, Swym reads a
   * hard `false` and deletes the heart before the banner is answered — and
   * the theme's later grant (on accept) cannot bring back a node that no
   * longer exists. That is the "heart mostly missing on mobile, fine on
   * desktop" report: desktop browsers tend to carry a stored consent, so the
   * grant lands before Swym looks. Intermittent because it is a race between
   * the theme's deny/grant calls and Swym's SDK init.
   *
   * The theme's denial is right and stays as it is. What is restored here is
   * only the CONSEQUENCE: once consent for preferences is actually granted and
   * the heart is missing, Swym's own public entry point is re-run
   * (`WishlistPlusPDPButtonAPI.initializePDPButton(_swat, true)` — the exact
   * call Swym makes from its SwymCallbacks) which recreates the button, wraps
   * the Add to Bag button with it, attaches Swym's listeners and re-runs its
   * consent check, now true. product.js's mutation observer then parks the new
   * button in the title slot and this file re-skins the icon, exactly as on a
   * first load. Nothing is granted or assumed on the shopper's behalf: the
   * restore runs only when Shopify itself reports preferences allowed.
   */
  function initConsentRecovery() {
    var RETRY_MS = 250;
    var MAX_RETRIES = 40; /* ~10s for the SDK to arrive after a grant */
    var pending = 0;

    function preferencesAllowed() {
      var privacy = window.Shopify && window.Shopify.customerPrivacy;
      if (!privacy || typeof privacy.preferencesProcessingAllowed !== 'function') return null;
      try { return privacy.preferencesProcessingAllowed(); } catch (error) { return null; }
    }

    function heartPresent() {
      return !!document.getElementById('swym-atw-button-container');
    }

    function attempt(retriesLeft) {
      pending = 0;
      if (heartPresent()) return;
      if (preferencesAllowed() !== true) {
        /* The theme's grant (setTrackingConsent) is asynchronous, so the
           consent event can arrive a beat before the API reflects it. */
        if (retriesLeft > 0) pending = setTimeout(function () { attempt(retriesLeft - 1); }, RETRY_MS);
        return;
      }
      var api = window.WishlistPlusPDPButtonAPI;
      var swat = window._swat;
      if (!api || typeof api.initializePDPButton !== 'function' || !swat) {
        if (retriesLeft > 0) pending = setTimeout(function () { attempt(retriesLeft - 1); }, RETRY_MS);
        return;
      }
      try {
        api.initializePDPButton(swat, true);
      } catch (error) {
        /* Swym's problem to report; the page must not break over a heart. */
      }
    }

    function scheduleAttempt() {
      if (pending) clearTimeout(pending);
      pending = setTimeout(function () { attempt(MAX_RETRIES); }, 0);
    }

    /* Every signal that consent may just have been granted: Cookiebot's own
       events (which the theme's sync also listens to), the theme's broadcast,
       and Swym's (fired when its own nudge popup is accepted). */
    ['CookiebotOnAccept', 'CookiebotOnConsentReady', 'CookiebotOnConsentChanged', 'lovisa:consent-updated']
      .forEach(function (name) { window.addEventListener(name, scheduleAttempt); });
    document.addEventListener('visitorConsentCollected', scheduleAttempt);
  }

  function init() {
    var slot = document.querySelector('[data-pdp-wishlist-slot]');
    if (!slot) return;

    initConsentRecovery();
    schedule();

    /* Watches the document, not the slot: Swym injects the button into its own
       mount point and product.js relocates it into the slot afterwards
       (moveSwymWishlistIntoSlot, src/assets/product.js), so the icon exists
       outside the slot first. Observing the document also survives Swym replacing
       the whole <svg> rather than rewriting its path — an observer bound to the
       svg would silently orphan itself, which is the trap ab-figma-usp.js
       L433-441 documents.
       rAF-coalesced so an SDK init burst, or Swym animating attributes on hover,
       collapses into one pass per frame; `style` is in the filter because that is
       how Swym hides or shrinks the icon. */
    var observer = new MutationObserver(schedule);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['viewBox', 'd', 'class', 'style', 'fill']
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
